import { useCurrent } from "@iscr/xAppLib/Hooks/useCurrent";
import React, { useCallback, useContext, useMemo } from "react";
import { Form, FormInput } from "semantic-ui-react";
import { GuardAction, useGuardAction } from "@iscr/xAppLib/UniForm/FormGuardActions";
import {useForm} from "../Hooks/useForm";
import UniFieldSUI from "./UniField-SUI";
import UniForm from "./UniForm";

const FormContext = React.createContext(null);

/**
 * @param {React.PropsWithChildren<Omit<UseFormConfig, 'submit'>> & { onSubmit: UseFormConfig.submit; fields: any[] }} props
 */
export function FormProvider(props) {
	const {children, fields, onSubmit, id, ...formProps} = props;

	const form = useForm({submit: onSubmit, ...formProps});
	const [action, setAction] = useGuardAction();

	useMountSection({
		section: props.section,
		active: props.enabled !== false && props.visible !== false,
		fields,
		form
	});

	return (
		<Form onSubmit={form.onSubmit} id={id} noValidate={props.novalidate}>
			<UniForm {...form} alwaysCheck fields={fields} onAction={setAction}>
				{(values, valids, uf_this, fields) => (
					<FormContext.Provider value={{form, values, valids, uf_this, fields}}>
						{children}
					</FormContext.Provider>
				)}
			</UniForm>
			{action && <GuardAction action={action}/>}
		</Form>
	);
}

export function FormContextProvider({children, fields, form, ...props}) {
	const [action, setAction] = useGuardAction();

	useMountSection({
		section: props.section,
		active: props.enabled !== false && props.visible !== false,
		fields,
		form
	});

	return (
		<>
			<UniForm {...form} alwaysCheck fields={fields} onAction={setAction} {...props}>
				{(values, valids, uf_this, fields) => (
					<FormContext.Provider value={{form, values, valids, uf_this, fields}}>
						{children}
					</FormContext.Provider>
				)}
			</UniForm>
			{action && <GuardAction action={action}/>}
		</>
	);
}

function useMountSection({ section, active = true, fields, form }) {
	const ref = useCurrent(form);

	React.useEffect(() => {
		const onMountSection = ref.current.onMountSection;
		if (!(section && active && onMountSection)) {
			return;
		}

		onMountSection(section, true, fields);
		return () => {
			onMountSection(section, false, fields);
		};
	}, [section, active, fields]);

	return null;
}

export function useFormContext() {
	const context = useContext(FormContext);
	if (!context) {
		throw new Error('useUniFormContext must be used within a UniFormProvider');
	}

	const onChange = (name, value) => context.uf_this.handleInputChange({target: {name, value}});

	return {...context, onChange};
}

export function useFormField(name) {
	const context = useFormContext();

	if (!context.fields[name]) {
		throw new Error(`Field "${name}" not found in form fields`);
	}

	const onChangeRef = useCurrent(context.onChange);
	const fieldsRef = useCurrent(context.fields);
	const setValue = useCallback(value => onChangeRef.current(name, value), [name]);
	const resetValue = useCallback(() => {
		const field = fieldsRef.current[name];
		setValue('value' in field ? field.value : null);
	}, [name, setValue]);
	const value = context.values[name];

	const inputValueFnRef = useCurrent(context.fields[name].value_for_input ?? (x => x));
	const valueForInput = useMemo(() => inputValueFnRef.current(value, context.values), [value, context.values]);

	const formattedValueFnRef = useCurrent(context.fields[name].format ?? (x => x));
	const formattedValue = useMemo(() => formattedValueFnRef.current(value, context.values), [value, context.values]);

	return {
		...context,
		value,
		setValue,
		resetValue,
		input: {
			...context.fields[name],
			name,
			value,
			valueForInput,
			formattedValue,
			setValue,
			onChange: context.uf_this.handleInputChange,
			required: typeof context.fields[name].required === 'function'
				? context.fields[name].required(context.values)
				: Boolean(context.fields[name].required),
			error: context.form.showErrors && context.valids[name] === false
				? context.form.validsErrors[name]
				: undefined,
			...(context.fields[name].pattern instanceof RegExp && {
				pattern: context.fields[name].pattern.toString().slice(1, -1),
			}),
		},
	};
}

export function BoundComponent({ as: Component, ...props}) {
	const {form} = useFormContext();

	return <Component {...form} {...props} />;
}

function BoundFieldComponent({name, as: Component, ...props}) {
	const {input} = useFormField(name);

	return <Component {...input} {...props} />;

}

function BoundUniFieldSUI(props) {
	const {values, valids, uf_this} = useFormContext();

	return <UniFieldSUI {...{values, valids, uf_this}} {...props} />;
}

export function BoundField(props) {
	if ('fl' in props) {
		return <BoundUniFieldSUI {...props} />;
	}

	return <BoundFieldComponent as={FormInput} {...props} />;
}
